// Adapter between LR *step* tag values and OpenLayers styling: decodes the
// sections with orbis-lr-sdk, works out the stretches over which every tag is
// constant (`stepSections.mjs`), and hands each stretch back to the caller as
// its own sliced geometry.
//
// Why per-section styles rather than one style per feature: a step tag such as
// `0-1000#yes;5000-7000#no` restricts only part of the lane. Colouring the
// whole feature from it would claim a restriction where there is none, and
// dropping it (what happened before — `"0-1000#yes" !== "yes"`) hid it
// entirely.
import { lengthOnPlaneCm } from "@orbis-lr-sdk/orbis-lr-sdk";
import { featureTo4326Geometry, sliceOlLineByCm } from "../../../util";
import { lrDecode } from "./lrDecoding";
import {
  isOffsetSectionedValue,
  mergeAdjacent,
  resolveSections,
} from "./stepSections.mjs";

export { isStepEncodedValue } from "./stepSections.mjs";

/**
 * Decode one step tag value into `{startCm, endCm, value}` sections. The SDK
 * needs the `:step` suffix on the key to pick the step interpolation — the WFS
 * hands us the decoded (suffix-less) key, so it is re-appended here.
 * @param {string} value
 * @returns {{startCm: number, endCm: number|null, value: string}[]}
 */
export function decodeStepSections(value) {
  let chain;
  try {
    chain = lrDecode("section:step", value);
  } catch {
    return [];
  }
  return chain.elements.map((element) => ({
    startCm: element.getStartDistance(),
    endCm:
      typeof element.getEndDistance === "function"
        ? element.getEndDistance()
        : null,
    value: element.getValue(),
  }));
}

// OL re-runs style functions on every render frame, and both LR decoding and
// line slicing are O(vertices). Memoising them per feature keeps panning cheap.
// Only the *inputs* are cached — never the Style objects, which must stay fresh
// so normal/hover/click frames don't share one array.
const decodeCache = new WeakMap();
const sliceCache = new WeakMap();

function cached(cache, feature, key, compute) {
  let perFeature = cache.get(feature);
  if (!perFeature) {
    perFeature = new Map();
    cache.set(feature, perFeature);
  }
  if (!perFeature.has(key)) {
    perFeature.set(key, compute());
  }
  return perFeature.get(key);
}

function getLengthCm(feature) {
  const cached = feature.get("lengthCm");
  if (Number.isFinite(cached) && cached > 0) return cached;
  try {
    return lengthOnPlaneCm(featureTo4326Geometry(feature));
  } catch {
    return 0;
  }
}

/**
 * Style a feature per step section.
 *
 * `classify` turns a set of resolved (scalar) tag values into a plain
 * descriptor — colour, width, whatever the caller needs — and `toStyles` turns
 * that descriptor into OL styles. Splitting the two lets neighbouring sections
 * that happen to look the same be merged before any Style is built.
 *
 * When the feature carries no step-encoded tag (the common case) this is
 * exactly `toStyles(classify(tags), undefined)` — no slicing, no behaviour
 * change, and `undefined` tells the caller to leave `Style.geometry` alone so
 * OL uses the feature's own geometry.
 *
 * @param {import("ol/Feature").default} feature
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @param {(tags: Record<string, string|undefined>) => object} classify
 * @param {(descriptor: object, geometry: import("ol/geom/LineString").default|undefined) => import("ol/style/Style").default[]} toStyles
 * @returns {import("ol/style/Style").default[]}
 */
export function styleBySection(feature, tags, classify, toStyles) {
  const wholeFeature = () => toStyles(classify(tags), undefined);

  const geometry = feature.getGeometry();
  if (!geometry || geometry.getType() !== "LineString") return wholeFeature();

  const sectionsByTag = {};
  const scalarTags = { ...tags };
  Object.keys(tags).forEach((name) => {
    // The looser test: these callers pass enumerated restriction tags, so the
    // bare-offset form is unambiguously a step section here. See
    // isOffsetSectionedValue for why it isn't safe to widen globally.
    if (!isOffsetSectionedValue(tags[name])) return;
    const sections = cached(decodeCache, feature, tags[name], () =>
      decodeStepSections(tags[name]),
    );
    if (sections.length === 0) return; // undecodable — leave it as a scalar
    sectionsByTag[name] = sections;
    // Outside its sections a step tag simply doesn't apply; the raw chain must
    // not leak into classify() as if it were a value.
    scalarTags[name] = undefined;
  });
  if (Object.keys(sectionsByTag).length === 0) return wholeFeature();

  const lengthCm = getLengthCm(feature);
  const intervals = resolveSections(sectionsByTag, lengthCm);
  if (intervals.length === 0) return wholeFeature();

  const described = intervals.map((interval) => ({
    ...interval,
    descriptor: classify({ ...scalarTags, ...interval.values }),
  }));
  // Merge neighbours that render identically, so a tag change that doesn't
  // change the styling doesn't fragment the line into extra sliced styles.
  return mergeAdjacent(described, (section) =>
    JSON.stringify(section.descriptor),
  )
    .map((section) =>
      toStyles(
        section.descriptor,
        cached(
          sliceCache,
          feature,
          `${geometry.getRevision()}:${section.startCm}-${section.endCm}`,
          () => sliceOlLineByCm(geometry, section.startCm, section.endCm),
        ),
      ),
    )
    .flat();
}
